
import {
  createContext,
  Key,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { local } from '@/pages/api/api';
import { OptionalType } from '@/types';
import { DateTime } from 'luxon';

export type ProtectionOrderItemType = {
  id: string | number;
  protection: OptionalType;
  displayOrder: number;
};

export type SettingsProviderProps = PropsWithChildren & {};

export type SettingsProviderContext = {
  creditCardDiscount: number;
  pixDiscount: number;
  setCreditCardDiscount: (discount: number) => void;
  setPixDiscount: (discount: number) => void;
  pagamentoPadrao: string;
  setPagamentoPadrao?: (pagamento: string) => void;
  isReady: boolean;
  specialProtection?: OptionalType;
  basicProtection?: OptionalType;
  flexivelProtection?: OptionalType;
  superiorProtection?: OptionalType;
  defaultSelectedProtection?: OptionalType;
  protectionOrderItems: ProtectionOrderItemType[];
  vantagens: {
    image_suaViagem_desktop?: any,
    image_suaViagem_mobile?: any,
    VantagensFoco: {
      id: Key | null | undefined;
      Icon: any,
      text: string
    }[],
    VantagensFoco_Text: string
  },
  highlight_optional?: OptionalType;
  highlight_text?: string;
  show_secondary_banners?: boolean;
  secondary_banners_start_date?: string;
  secondary_banners_end_date?: string;
  secondary_banner_desktop?: any;
  secondary_banner_mobile?: any;
  specialProtectionDiscount?: string;
  flexivelProtectionDiscount?: string;
  cashbackPercentage?: number;
  helpModalPage?: string;
  helpModalIdleTimeSeconds?: number;
}

const Context = createContext<SettingsProviderContext>({} as SettingsProviderContext);

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [creditCardDiscount, setCreditCardDiscount] = useState<number>(0);
  const [pixDiscount, setPixDiscount] = useState<number>(0);
  const [pagamentoPadrao, setPagamentoPadrao] = useState<string>('');
  const [isReady, setIsReady] = useState(false);
  const [specialProtection, setSpecialProtection] = useState<OptionalType | undefined>();
  const [basicProtection, setBasicProtection] = useState<OptionalType | undefined>();
  const [flexivelProtection, setFlexivelProtection] = useState<OptionalType | undefined>();
  const [superiorProtection, setSuperiorProtection] = useState<OptionalType | undefined>();
  const [defaultSelectedProtection, setDefaultSelectedProtection] = useState<OptionalType | undefined>();
  const [protectionOrderItems, setProtectionOrderItems] = useState<ProtectionOrderItemType[]>([]);
  const [vantagens, setVantagens] = useState({
    image_suaViagem_desktop: undefined,
    image_suaViagem_mobile: undefined,
    VantagensFoco: [],
    VantagensFoco_Text: ''
  })
  const [highlight_optional, setHighlightOptional] = useState<OptionalType | undefined>();
  const [highlight_text, setHighlightText] = useState<string | undefined>();
  const [show_secondary_banners, setShowSecondaryBanners] = useState<boolean>(false);
  const [secondary_banners_start_date, setSecondaryBannersStartDate] = useState<string | undefined>();
  const [secondary_banners_end_date, setSecondaryBannersEndDate] = useState<string | undefined>();
  const [secondary_banner_desktop, setSecondaryBannerDesktop] = useState<any>();
  const [secondary_banner_mobile, setSecondaryBannerMobile] = useState<any>();
  const [specialProtectionDiscount, setSpecialProtectionDiscount] = useState<string | undefined>();
  const [flexivelProtectionDiscount, setFlexivelProtectionDiscount] = useState<string | undefined>();
  const [cashbackPercentage, setCashbackPercentage] = useState<number>(15);
  const [helpModalPage, setHelpModalPage] = useState<string | undefined>();
  const [helpModalIdleTimeSeconds, setHelpModalIdleTimeSeconds] = useState<number>(15);

  useEffect(() => {
    async function fetchSettings() {
      try {
        const {
          data: {
            credit_card_discount,
            pix_discount,
            PagamentoPadrao,
            specialProtection: fetchedSpecialProtection,
            basicProtection: fetchedBasicProtection,
            flexivelProtection: fetchedFlexivelProtection,
            superiorProtection: fetchedSuperiorProtection,
            defaultSelectedProtection: fetchedDefaultSelectedProtection,
            protectionOrderItems: fetchedProtectionOrderItems,
            highlight_optional: fetchedHighlightOptional,
            highlight_text: fetchedHighlightText,
            show_secondary_banners: fetchedShowSecondaryBanners,
            secondary_banners_start_date: fetchedSecondaryBannersStartDate,
            secondary_banners_end_date: fetchedSecondaryBannersEndDate,
            secondary_banner_desktop: fetchedSecondaryBannerDesktop,
            secondary_banner_mobile: fetchedSecondaryBannerMobile,
            special_protection_discount,
            flexivel_protection_discount,
            cashback_percentage,
            help_modal_page,
            help_modal_idle_time_seconds,
            ...rest
          },
        } = await local('/api/settings');

        setCreditCardDiscount(credit_card_discount);
        setPixDiscount(pix_discount);
        setPagamentoPadrao(PagamentoPadrao);
        setSpecialProtection(fetchedSpecialProtection);
        setBasicProtection(fetchedBasicProtection);
        setFlexivelProtection(fetchedFlexivelProtection);
        setSuperiorProtection(fetchedSuperiorProtection);
        setDefaultSelectedProtection(fetchedDefaultSelectedProtection);
        setProtectionOrderItems(fetchedProtectionOrderItems || []);
        setVantagens({
          image_suaViagem_desktop: rest.image_suaViagem_desktop,
          image_suaViagem_mobile: rest.image_suaViagem_mobile,
          VantagensFoco: rest.VantagensFoco,
          VantagensFoco_Text: rest.VantagensFoco_Text
        })
        setHighlightOptional(fetchedHighlightOptional);
        setHighlightText(fetchedHighlightText);
        setShowSecondaryBanners(fetchedShowSecondaryBanners);

        if (fetchedSecondaryBannersStartDate) {
          const startDate = DateTime.fromISO(fetchedSecondaryBannersStartDate);
          setSecondaryBannersStartDate(startDate.toISO() ?? undefined);
        } else {
          setSecondaryBannersStartDate(fetchedSecondaryBannersStartDate);
        }

        if (fetchedSecondaryBannersEndDate) {
          const endDate = DateTime.fromISO(fetchedSecondaryBannersEndDate);
          setSecondaryBannersEndDate(endDate.toISO() ?? undefined);
        } else {
          setSecondaryBannersEndDate(fetchedSecondaryBannersEndDate);
        }

        setSecondaryBannerDesktop(fetchedSecondaryBannerDesktop);
        setSecondaryBannerMobile(fetchedSecondaryBannerMobile);
        setSpecialProtectionDiscount(special_protection_discount);
        setFlexivelProtectionDiscount(flexivel_protection_discount);
        setCashbackPercentage(cashback_percentage || 15);
        setHelpModalPage(help_modal_page);
        setHelpModalIdleTimeSeconds(help_modal_idle_time_seconds || 15);
        setIsReady(true);
      } catch (err) {
        console.log('settings fetch err', `${err}`);
      }
    }

    fetchSettings();
  }, []);

  return (
    <Context.Provider
      value={{
        creditCardDiscount,
        pixDiscount,
        setCreditCardDiscount,
        setPixDiscount,
        pagamentoPadrao,
        setPagamentoPadrao,
        isReady,
        specialProtection,
        basicProtection,
        flexivelProtection,
        superiorProtection,
        defaultSelectedProtection,
        protectionOrderItems,
        vantagens,
        highlight_optional,
        highlight_text,
        show_secondary_banners,
        secondary_banners_start_date,
        secondary_banners_end_date,
        secondary_banner_desktop,
        secondary_banner_mobile,
        specialProtectionDiscount,
        flexivelProtectionDiscount,
        cashbackPercentage,
        helpModalPage,
        helpModalIdleTimeSeconds
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function useSettings() {
  return useContext(Context);
}
